import React, { useEffect, useRef } from 'react';
import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { Arrow, Circle, Ellipse, Group, Image as KonvaImage, Label, Rect, Tag, Text } from 'react-konva';
import {
  AnnotationShape,
  ArrowShape,
  BoxShape,
  MarkerShape,
  TextShape,
  contrastTextColor,
} from './types';

export type ShapePatch = Record<string, unknown>;

export interface ShapeNodeProps {
  shape: AnnotationShape;
  image: HTMLImageElement;
  imageBounds: { width: number; height: number };
  /** Blockgroesse fuer Verpixelung (pro Bild konstant, damit alle Bereiche gleich wirken). */
  pixelSize: number;
  /** Anzeige-Skalierung der Stage; Trefferflaechen werden damit zoom-unabhaengig in Bildschirmpixeln bemessen. */
  scale: number;
  /** false waehrend Zeichnen-Vorschau / Zuschneiden: keine Auswahl, kein Drag. */
  interactive: boolean;
  selected: boolean;
  registerNode: (id: string, node: Konva.Node | null) => void;
  onSelect: (id: string) => void;
  onChange: (id: string, patch: ShapePatch) => void;
  onTextEdit?: (id: string) => void;
}

const SPOTLIGHT_DIM = 'rgba(0,0,0,0.6)';
/** Mindest-Trefferbreite von Linien/Konturen in Bildschirmpixeln. */
const HIT_AREA_MIN_PX = 16;
const ARROW_HIT_MIN_PX = 22;
const hitWidth = (strokeWidth: number, scale: number, minPx: number) => Math.max(strokeWidth, minPx / Math.max(scale, 0.01));
/** Ab dieser Kantenlaenge (Bildpixel) wird der Pixelate-Filter tatsaechlich angewendet. */
const PIXELATE_MIN_SIZE = 4;
/** Unsichtbare Fuellung: macht das Innere von Rahmen/Ellipsen im Auswahl-Werkzeug anklickbar. */
const TRANSPARENT_HIT_FILL = 'rgba(0,0,0,0)';

const selectOnMouseDown = (onSelect: (id: string) => void, id: string) => (e: KonvaEventObject<MouseEvent>) => {
  e.cancelBubble = true;
  onSelect(id);
};

/* ── Box-artige Formen ─────────────────────────────────────── */

const boxFromNode = (n: Konva.Node): { x: number; y: number; width: number; height: number } => {
  const width = Math.max(4, n.width() * n.scaleX());
  const height = Math.max(4, n.height() * n.scaleY());
  n.scaleX(1);
  n.scaleY(1);
  return { x: n.x(), y: n.y(), width, height };
};

const commonBoxHandlers = (
  shape: BoxShape,
  props: ShapeNodeProps,
) => ({
  draggable: props.interactive,
  listening: props.interactive,
  onMouseDown: selectOnMouseDown(props.onSelect, shape.id),
  onDragStart: () => props.onSelect(shape.id),
  onDragEnd: (e: KonvaEventObject<DragEvent>) => {
    props.onChange(shape.id, { x: e.target.x(), y: e.target.y() });
  },
  onTransformEnd: (e: KonvaEventObject<Event>) => {
    props.onChange(shape.id, boxFromNode(e.target));
  },
});

const RectFrame: React.FC<ShapeNodeProps & { shape: BoxShape }> = (props) => {
  const { shape } = props;
  return (
    <Rect
      ref={(n) => props.registerNode(shape.id, n)}
      x={shape.x}
      y={shape.y}
      width={shape.width}
      height={shape.height}
      stroke={shape.color}
      strokeWidth={shape.strokeWidth}
      hitStrokeWidth={hitWidth(shape.strokeWidth, props.scale, HIT_AREA_MIN_PX)}
      fill={TRANSPARENT_HIT_FILL}
      cornerRadius={Math.min(4, shape.strokeWidth)}
      strokeScaleEnabled={false}
      {...commonBoxHandlers(shape, props)}
    />
  );
};

const EllipseFrame: React.FC<ShapeNodeProps & { shape: BoxShape }> = (props) => {
  const { shape } = props;
  const handlers = commonBoxHandlers(shape, props);
  return (
    <Ellipse
      ref={(n) => props.registerNode(shape.id, n)}
      x={shape.x + shape.width / 2}
      y={shape.y + shape.height / 2}
      radiusX={shape.width / 2}
      radiusY={shape.height / 2}
      stroke={shape.color}
      strokeWidth={shape.strokeWidth}
      hitStrokeWidth={hitWidth(shape.strokeWidth, props.scale, HIT_AREA_MIN_PX)}
      fill={TRANSPARENT_HIT_FILL}
      strokeScaleEnabled={false}
      {...handlers}
      onDragEnd={(e) => {
        const n = e.target as Konva.Ellipse;
        props.onChange(shape.id, { x: n.x() - shape.width / 2, y: n.y() - shape.height / 2 });
      }}
      onTransformEnd={(e) => {
        const n = e.target as Konva.Ellipse;
        const width = Math.max(4, n.radiusX() * 2 * n.scaleX());
        const height = Math.max(4, n.radiusY() * 2 * n.scaleY());
        n.scaleX(1);
        n.scaleY(1);
        props.onChange(shape.id, { x: n.x() - width / 2, y: n.y() - height / 2, width, height });
      }}
    />
  );
};

const Highlight: React.FC<ShapeNodeProps & { shape: BoxShape }> = (props) => {
  const { shape } = props;
  return (
    <Rect
      ref={(n) => props.registerNode(shape.id, n)}
      x={shape.x}
      y={shape.y}
      width={shape.width}
      height={shape.height}
      fill={shape.color}
      opacity={0.45}
      globalCompositeOperation="multiply"
      {...commonBoxHandlers(shape, props)}
    />
  );
};

const Redact: React.FC<ShapeNodeProps & { shape: BoxShape }> = (props) => {
  const { shape } = props;
  return (
    <Rect
      ref={(n) => props.registerNode(shape.id, n)}
      x={shape.x}
      y={shape.y}
      width={shape.width}
      height={shape.height}
      fill={shape.color}
      {...commonBoxHandlers(shape, props)}
    />
  );
};

/**
 * Verpixelter Bereich: Ausschnitt des Originalbilds mit Pixelate-Filter.
 * Konva-Filter benoetigen `cache()`; nach jeder Geometrie-Aenderung neu cachen.
 */
const PixelateRegion: React.FC<ShapeNodeProps & { shape: BoxShape }> = (props) => {
  const { shape, image, pixelSize } = props;
  const nodeRef = useRef<Konva.Image | null>(null);
  const b = clampToImage(shape, props.imageBounds);
  // Beim Aufziehen ist der Bereich anfangs (fast) leer. Konva legt fuer den Filter-Cache ein Canvas
  // in Bildschirm-Pixeln an, das bei so kleinen Flaechen auf 0 px abgerundet werden kann und
  // drawImage mit InvalidStateError abbrechen laesst. Daher erst ab einer Mindestgroesse filtern.
  const tooSmall = b.width < PIXELATE_MIN_SIZE || b.height < PIXELATE_MIN_SIZE;

  useEffect(() => {
    const node = nodeRef.current;
    if (!node || tooSmall) return;
    try {
      node.cache();
    } catch {
      node.clearCache();
    }
    node.getLayer()?.batchDraw();
  }, [b.x, b.y, b.width, b.height, image, pixelSize, tooSmall]);

  if (tooSmall) {
    return (
      <Rect
        ref={(n) => props.registerNode(shape.id, n)}
        x={b.x}
        y={b.y}
        width={b.width}
        height={b.height}
        fill="rgba(128,128,128,0.35)"
        stroke="#1e88e5"
        strokeWidth={1}
        dash={[4, 3]}
        strokeScaleEnabled={false}
        {...commonBoxHandlers(shape, props)}
      />
    );
  }

  return (
    <KonvaImage
      ref={(n) => {
        nodeRef.current = n;
        props.registerNode(shape.id, n);
      }}
      image={image}
      x={b.x}
      y={b.y}
      width={b.width}
      height={b.height}
      crop={{ x: b.x, y: b.y, width: b.width, height: b.height }}
      filters={[Konva.Filters.Pixelate]}
      pixelSize={pixelSize}
      stroke={props.selected ? '#1e88e5' : undefined}
      strokeWidth={props.selected ? 1 : 0}
      strokeScaleEnabled={false}
      {...commonBoxHandlers(shape, props)}
    />
  );
};

const clampToImage = (shape: BoxShape, bounds: { width: number; height: number }) => {
  const x = Math.max(0, Math.min(shape.x, bounds.width - 1));
  const y = Math.max(0, Math.min(shape.y, bounds.height - 1));
  const width = Math.max(1, Math.min(shape.width, bounds.width - x));
  const height = Math.max(1, Math.min(shape.height, bounds.height - y));
  return { x, y, width, height };
};

/** Spotlight: alles ausser dem Bereich abdunkeln (4 Rechtecke), Bereich selbst ist Interaktionsziel. */
const Spotlight: React.FC<ShapeNodeProps & { shape: BoxShape }> = (props) => {
  const { shape, imageBounds } = props;
  const W = imageBounds.width;
  const H = imageBounds.height;
  const right = shape.x + shape.width;
  const bottom = shape.y + shape.height;
  return (
    <Group>
      <Rect x={0} y={0} width={W} height={Math.max(0, shape.y)} fill={SPOTLIGHT_DIM} listening={false} />
      <Rect x={0} y={bottom} width={W} height={Math.max(0, H - bottom)} fill={SPOTLIGHT_DIM} listening={false} />
      <Rect x={0} y={shape.y} width={Math.max(0, shape.x)} height={shape.height} fill={SPOTLIGHT_DIM} listening={false} />
      <Rect x={right} y={shape.y} width={Math.max(0, W - right)} height={shape.height} fill={SPOTLIGHT_DIM} listening={false} />
      <Rect
        ref={(n) => props.registerNode(shape.id, n)}
        x={shape.x}
        y={shape.y}
        width={shape.width}
        height={shape.height}
        fill={TRANSPARENT_HIT_FILL}
        stroke={props.selected ? '#ffffff' : undefined}
        strokeWidth={props.selected ? 1 : 0}
        dash={[6, 4]}
        strokeScaleEnabled={false}
        {...commonBoxHandlers(shape, props)}
      />
    </Group>
  );
};

/* ── Pfeil ─────────────────────────────────────────────────── */

const ArrowNode: React.FC<ShapeNodeProps & { shape: ArrowShape }> = (props) => {
  const { shape } = props;
  const head = shape.strokeWidth * 3 + 6;
  return (
    <Arrow
      ref={(n) => props.registerNode(shape.id, n)}
      points={[...shape.points]}
      stroke={shape.color}
      fill={shape.color}
      strokeWidth={shape.strokeWidth}
      pointerLength={head}
      pointerWidth={head}
      lineCap="round"
      lineJoin="round"
      hitStrokeWidth={hitWidth(shape.strokeWidth, props.scale, ARROW_HIT_MIN_PX)}
      draggable={props.interactive}
      listening={props.interactive}
      onMouseDown={selectOnMouseDown(props.onSelect, shape.id)}
      onDragStart={() => props.onSelect(shape.id)}
      onDragEnd={(e) => {
        const n = e.target;
        const dx = n.x();
        const dy = n.y();
        n.position({ x: 0, y: 0 });
        const [x1, y1, x2, y2] = shape.points;
        props.onChange(shape.id, { points: [x1 + dx, y1 + dy, x2 + dx, y2 + dy] });
      }}
    />
  );
};

/* ── Text-Label ────────────────────────────────────────────── */

const TextLabel: React.FC<ShapeNodeProps & { shape: TextShape }> = (props) => {
  const { shape } = props;
  return (
    <Label
      ref={(n) => props.registerNode(shape.id, n)}
      x={shape.x}
      y={shape.y}
      draggable={props.interactive}
      listening={props.interactive}
      onMouseDown={selectOnMouseDown(props.onSelect, shape.id)}
      onDragStart={() => props.onSelect(shape.id)}
      onDragEnd={(e) => props.onChange(shape.id, { x: e.target.x(), y: e.target.y() })}
      onDblClick={() => props.onTextEdit?.(shape.id)}
    >
      <Tag fill={shape.color} cornerRadius={Math.max(3, shape.fontSize * 0.2)} />
      <Text
        text={shape.text || ' '}
        fontSize={shape.fontSize}
        fontStyle="bold"
        fontFamily="Inter, Roboto, Helvetica, Arial, sans-serif"
        fill={contrastTextColor(shape.color)}
        padding={Math.round(shape.fontSize * 0.35)}
      />
    </Label>
  );
};

/* ── Nummerierter Marker ───────────────────────────────────── */

const NumberMarker: React.FC<ShapeNodeProps & { shape: MarkerShape }> = (props) => {
  const { shape } = props;
  const r = shape.radius;
  return (
    <Group
      ref={(n) => props.registerNode(shape.id, n)}
      x={shape.x}
      y={shape.y}
      draggable={props.interactive}
      listening={props.interactive}
      onMouseDown={selectOnMouseDown(props.onSelect, shape.id)}
      onDragStart={() => props.onSelect(shape.id)}
      onDragEnd={(e) => props.onChange(shape.id, { x: e.target.x(), y: e.target.y() })}
    >
      <Circle
        radius={r}
        fill={shape.color}
        stroke={contrastTextColor(shape.color)}
        strokeWidth={Math.max(2, r * 0.12)}
        shadowColor="rgba(0,0,0,0.45)"
        shadowBlur={r * 0.4}
        shadowOffsetY={r * 0.1}
      />
      <Text
        text={String(shape.number)}
        fontSize={r * 1.25}
        fontStyle="bold"
        fontFamily="Inter, Roboto, Helvetica, Arial, sans-serif"
        fill={contrastTextColor(shape.color)}
        width={r * 2}
        height={r * 2}
        offsetX={r}
        offsetY={r}
        align="center"
        verticalAlign="middle"
        listening={false}
      />
    </Group>
  );
};

/* ── Dispatcher ────────────────────────────────────────────── */

export const ShapeNode: React.FC<ShapeNodeProps> = (props) => {
  const { shape } = props;
  switch (shape.type) {
    case 'rect':
      return <RectFrame {...props} shape={shape} />;
    case 'ellipse':
      return <EllipseFrame {...props} shape={shape} />;
    case 'highlight':
      return <Highlight {...props} shape={shape} />;
    case 'redact':
      return <Redact {...props} shape={shape} />;
    case 'pixelate':
      return <PixelateRegion {...props} shape={shape} />;
    case 'spotlight':
      return <Spotlight {...props} shape={shape} />;
    case 'arrow':
      return <ArrowNode {...props} shape={shape} />;
    case 'text':
      return <TextLabel {...props} shape={shape} />;
    case 'marker':
      return <NumberMarker {...props} shape={shape} />;
    default:
      return null;
  }
};

/* ── Pfeil-Endpunkt-Griffe (UI-Layer) ──────────────────────── */

interface ArrowHandlesProps {
  shape: ArrowShape;
  /** Bildschirm-Skalierung, damit Griffe unabhaengig vom Zoom gleich gross bleiben. */
  scale: number;
  getArrowNode: () => Konva.Arrow | null;
  onChange: (id: string, patch: ShapePatch) => void;
}

export const ArrowHandles: React.FC<ArrowHandlesProps> = ({ shape, scale, getArrowNode, onChange }) => {
  const radius = 8 / scale;
  const [x1, y1, x2, y2] = shape.points;

  const makeHandle = (index: 0 | 1) => {
    const x = index === 0 ? x1 : x2;
    const y = index === 0 ? y1 : y2;
    const livePoints = (nx: number, ny: number): [number, number, number, number] =>
      index === 0 ? [nx, ny, x2, y2] : [x1, y1, nx, ny];
    return (
      <Circle
        key={index}
        x={x}
        y={y}
        radius={radius}
        fill="#ffffff"
        stroke="#1e88e5"
        strokeWidth={2 / scale}
        draggable
        onMouseDown={(e) => {
          e.cancelBubble = true;
        }}
        onDragMove={(e) => {
          const arrow = getArrowNode();
          if (arrow) arrow.points(livePoints(e.target.x(), e.target.y()));
        }}
        onDragEnd={(e) => {
          onChange(shape.id, { points: livePoints(e.target.x(), e.target.y()) });
        }}
      />
    );
  };

  return (
    <>
      {makeHandle(0)}
      {makeHandle(1)}
    </>
  );
};
